import { useContext, useEffect, useMemo, useState } from 'react';
import { FormContext } from '../contexts';
import { getFieldIdWithoutIndex, requiresAnEntry } from '../utils';
import { useNotApplicableField } from './use-not-appplicable-field';
import {
  ArrayFormField,
  FormFieldType,
  StagedFieldsGroupFormField,
} from '../typings';
import type { FormInitialValues } from '../typings';
import { useFieldHasAnyValue } from './use-field-has-any-value';
import { useRequiredField } from './use-required-field';
import { useShouldValidateField } from './use-should-validate-field';
import { useFormValues, useFormActions } from '../core';
import { config } from '../config';
import { formFieldHasFormikValue } from '../utils/formFieldHasFormikValue';

export const useMultipleField = (
  field: ArrayFormField | StagedFieldsGroupFormField,
) => {
  const { maxItems } = useContext(FormContext);

  const isFieldNA = useNotApplicableField(field.notApplicableField?.fieldId);

  const hasAnyValue = useFieldHasAnyValue(field.fieldId);

  const [isLastItem, setIsLastItem] = useState(false);

  const [forcedShow, setForcedShow] = useState(false);

  // An optional group with no entry yet shows only its Add button.
  const isEmptyOptionalGroup =
    field.type === FormFieldType.Array &&
    field.minItems === 0 &&
    (field as ArrayFormField).isEmpty;

  // An entry the provider added stays on screen, even blank behind a question.
  const isAddedEntry =
    field.type === FormFieldType.Array &&
    (field as ArrayFormField).providerAdded === true;

  const shouldBeVisible = useMemo(
    () =>
      !isFieldNA &&
      (hasAnyValue ||
        isAddedEntry ||
        (!field.questionIfOptional && !isEmptyOptionalGroup) ||
        (field.index === 0 && !isLastItem) ||
        field.index > 0 ||
        forcedShow),
    [
      isFieldNA,
      hasAnyValue,
      isLastItem,
      forcedShow,
      field,
      isEmptyOptionalGroup,
      isAddedEntry,
    ],
  );

  const [isArrayVisible, setIsArrayVisible] = useState(shouldBeVisible);

  const isStagedFieldsGroup = useMemo(
    () => field.type === FormFieldType.StagedFieldsGroup,
    [field.type],
  );

  // Arrays enforce completeness through _atLeastOneComplete, so their _required always passes.
  const requiresEntry =
    field.type === FormFieldType.Array ? false : requiresAnEntry(field);

  const { requiredFieldId } = useRequiredField(
    field.fieldId,
    requiresEntry,
    isFieldNA,
    hasAnyValue,
    field.index,
  );

  useShouldValidateField(
    field.fieldId,
    isStagedFieldsGroup ? hasAnyValue ?? false : !isFieldNA && isArrayVisible,
  );

  useEffect(() => {
    const itemId = getFieldIdWithoutIndex(field.fieldId);
    setIsLastItem(maxItems[itemId]?.id === field.fieldId);
  }, [maxItems, field.fieldId]);

  useEffect(() => {
    setIsArrayVisible(shouldBeVisible);
  }, [shouldBeVisible]);

  // Maintain _instanceHasValue from live Formik values for array instances.
  const values = useFormValues<FormInitialValues>();
  const { setFieldValue } = useFormActions();

  useEffect(() => {
    if (field.type !== FormFieldType.Array) return;

    const instanceHasValue = formFieldHasFormikValue(
      field as ArrayFormField,
      values as Record<string, unknown>,
    );

    setFieldValue(
      `${field.fieldId}${config.instanceHasValueFieldSuffix}`,
      instanceHasValue,
      false, // skip validation to avoid triggering an infinite loop
    );
  }, [values, field, setFieldValue]);

  return {
    requiredFieldId,
    isFieldNA,
    isArrayVisible,
    isLastItem,
    setForcedShow,
    hasAnyValue,
  };
};
